import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { useSEO } from '../lib/seo';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

const sections: Array<{ title: string; links: Array<{ to: string; label: string }> }> = [
  {
    title: 'Core',
    links: [
      { to: '/', label: 'Home' },
      { to: '/early-access', label: 'Early access landing' },
      { to: '/pricing', label: 'Pricing' },
      { to: '/about', label: 'About' },
      { to: '/faq', label: 'FAQ' },
      { to: '/contact', label: 'Contact' },
    ],
  },
  {
    title: 'Marketing Content',
    links: [
      { to: '/blog', label: 'Blog' },
      { to: '/resources', label: 'Resources' },
      { to: '/guides', label: 'Guides' },
      { to: '/press', label: 'Press' },
      { to: '/best-relationship-app', label: 'Best relationship app' },
      { to: '/best-couples-app', label: 'Best couples app' },
      { to: '/compare/tension-vs-paired', label: 'TENSION vs Paired' },
      { to: '/alternatives/paired', label: 'Paired alternatives' },
      { to: '/enterprise', label: 'TENSION Enterprise' },
      { to: '/enterprise/solutions', label: 'Enterprise solutions' },
      { to: '/enterprise/security', label: 'Enterprise security' },
      { to: '/enterprise/pilot', label: 'Enterprise pilot program' },
      { to: '/enterprise/platform', label: 'Enterprise platform & integrations' },
      { to: '/enterprise/identity', label: 'Enterprise identity (SSO scaffold)' },
      { to: '/enterprise/whitelabel', label: 'Enterprise white-label profile' },
      { to: '/enterprise/partner-docs', label: 'Enterprise partner docs' },
      { to: '/enterprise/expansion', label: 'Enterprise expansion programs' },
      { to: '/business-inquiries', label: 'Investors & business inquiries' },
    ],
  },
  {
    title: 'Feature Pages',
    links: [
      { to: '/features/relationship-intelligence', label: 'Relationship Intelligence Suite' },
      { to: '/features/conversation-micro-coach', label: 'Conversation Micro-Coach' },
      { to: '/relationship-intelligence/faq', label: 'Relationship Intelligence FAQ' },
    ],
  },
  {
    title: 'Community',
    links: [
      { to: '/community', label: 'Social network' },
      { to: '/community/legacy', label: 'Legacy community archive' },
      { to: '/support', label: 'Support' },
      { to: '/help/sync', label: 'Sync & personalization help' },
      { to: '/feedback', label: 'Feedback' },
    ],
  },
];

export default function SiteMap() {
  useSEO({
    title: 'Site Map | TENSION',
    description:
      'Explore all major TENSION pages including marketing content, feature pages, and support links.',
    keywords: ['site map', 'tension pages', 'tension links'],
    canonical: '/site-map',
    ogType: 'website',
    twitterCard: 'summary',
  });

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-10">
        <StockHeroStrip stockKey="coupleOutdoor" alt="Illustrative stock imagery" />
        <header className="rounded-2xl border border-violet-100 bg-white/90 p-6 shadow-md backdrop-blur">
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-sm font-semibold text-violet-700">TENSION</p>
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Site Map</h1>
          <p className="text-neutral-600 mt-2">
            A quick index of key pages for visitors and search engines.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-4">
          {sections.map((section) => (
            <Card key={section.title} className="p-5 border-violet-100 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-lg font-semibold text-neutral-900">{section.title}</h2>
              <ul className="mt-3 space-y-2">
                {section.links.map((entry) => (
                  <li key={entry.to}>
                    <Link to={entry.to} className="text-violet-700 underline hover:text-violet-800">
                      {entry.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}
